import { useState } from 'react';
import { Button, Input, Space } from 'antd';
import { HolderOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

// Pure list helpers (unit-tested). Each returns a NEW array (or the same
// reference on a no-op) so React sees the change / skips it correctly.
export function moveTo(arr: string[], from: number, to: number): string[] {
  if (from === to || from < 0 || from >= arr.length || to < 0 || to >= arr.length) return arr;
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
export function removeAt(arr: string[], i: number): string[] {
  if (i < 0 || i >= arr.length) return arr;
  return arr.slice(0, i).concat(arr.slice(i + 1));
}
export function updateAt(arr: string[], i: number, val: string): string[] {
  if (i < 0 || i >= arr.length) return arr;
  const next = arr.slice();
  next[i] = val;
  return next;
}
export function append(arr: string[], val: string): string[] {
  const t = val.trim();
  if (!t) return arr;
  return arr.concat(t);
}
// Split a (possibly multi-line, pasted) string into trimmed non-empty items
// and append them all — preserves the "paste a block of bullets" workflow.
export function appendMany(arr: string[], raw: string): string[] {
  const parts = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return parts.length ? arr.concat(parts) : arr;
}

interface Props {
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  placeholder?: string;
}

/**
 * Reorderable string-list editor: drag the grip handle to reorder rows, edit
 * each item inline, delete per row, add via the trailing input (Enter / paste a
 * newline block splits into rows). Replaces the append-only `Select mode="tags"`
 * where display order matters. Native HTML5 drag-and-drop — no extra dependency.
 */
export function OrderedListEditor({ value, onChange, placeholder }: Props) {
  const items = value ?? [];
  const [draft, setDraft] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const add = () => {
    const next = appendMany(items, draft);
    if (next !== items) {
      onChange(next);
      setDraft('');
    }
  };
  // A paste that brings in newlines splits into rows immediately.
  const onDraftChange = (raw: string) => {
    if (/\r?\n/.test(raw)) {
      const next = appendMany(items, raw);
      if (next !== items) onChange(next);
      setDraft('');
    } else {
      setDraft(raw);
    }
  };

  const drop = (to: number) => {
    if (dragIndex !== null && dragIndex !== to) onChange(moveTo(items, dragIndex, to));
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>
      {items.map((item, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          onDragOver={(ev) => { if (dragIndex !== null) { ev.preventDefault(); if (overIndex !== i) setOverIndex(i); } }}
          onDrop={(ev) => { ev.preventDefault(); drop(i); }}
          style={{
            display: 'flex',
            gap: 4,
            alignItems: 'center',
            opacity: dragIndex === i ? 0.4 : 1,
            // insertion indicator on the hovered target row
            borderTop: overIndex === i && dragIndex !== null && dragIndex !== i ? '2px solid #1677ff' : '2px solid transparent',
            borderRadius: 4,
          }}
        >
          <Button
            icon={<HolderOutlined />}
            draggable
            onDragStart={(ev) => { setDragIndex(i); ev.dataTransfer.effectAllowed = 'move'; }}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            style={{ cursor: 'grab' }}
            title="拖动排序"
          />
          <Input
            style={{ flex: 1 }}
            value={item}
            onChange={(ev) => onChange(updateAt(items, i, ev.target.value))}
            onBlur={() => { if (!item.trim()) onChange(removeAt(items, i)); }}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={() => onChange(removeAt(items, i))}
            title="删除"
          />
        </div>
      ))}
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={draft}
          onChange={(ev) => onDraftChange(ev.target.value)}
          onPressEnter={add}
          placeholder={placeholder ?? 'Add an item and press Enter'}
        />
        <Button icon={<PlusOutlined />} onClick={add}>Add</Button>
      </Space.Compact>
    </Space>
  );
}
